import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Button, Card, CardContent, Fade } from '@mui/material';
import { getAllWallets } from '../../services/financeService';
import AddCreditAdminModal from '../../components/Admin/AddCreditAdminModal';
import { AccountBalanceWallet } from '@mui/icons-material';

const AllWalletsPage = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalWallets, setTotalWallets] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isAddCreditModalOpen, setAddCreditModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchWallets = async (currentPage, currentRowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllWallets(currentPage + 1, currentRowsPerPage);
      setWallets(response.data || []);
      setTotalWallets(response.total || 0);
    } catch (err) {
      setError('Error al cargar las billeteras.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleOpenRechargeModal = (userId) => {
    setSelectedUserId(userId);
    setAddCreditModalOpen(true);
  };

  const handleCreditAdded = () => {
    setAddCreditModalOpen(false);
    fetchWallets(page, rowsPerPage);
    setSuccessMessage('Recarga exitosa.');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <Fade in timeout={600}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2.5, sm: 4 }
        }}>
          <Box
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AccountBalanceWallet sx={{
              color: '#FFFFFF',
              fontSize: { xs: '1.75rem', sm: '2rem' }
            }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#002642',
                fontSize: { xs: '1.375rem', sm: '2rem', md: '2.5rem' },
                mb: { xs: 0.25, sm: 0.5 },
                lineHeight: { xs: 1.2, sm: 1.1 }
              }}
            >
              Todas las Billeteras
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#75787B',
                fontSize: { xs: '0.8rem', sm: '1rem' },
                lineHeight: 1.3
              }}
            >
              Gestiona el saldo y las transacciones de los usuarios
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>{successMessage}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress size={60} sx={{ color: '#002642' }} />
          </Box>
        ) : (
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: { xs: 0, sm: 0 } }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID Usuario</TableCell>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Saldo</TableCell>
                      <TableCell>Límite de Crédito</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(wallets) && wallets.map((wallet) => (
                      <TableRow key={wallet.wallet_id}>
                        <TableCell>{wallet.user_id}</TableCell>
                        <TableCell>{wallet.user?.username || `ID: ${wallet.user_id}`}</TableCell>
                        <TableCell>${typeof wallet.balance === 'number' ? wallet.balance.toFixed(2) : parseFloat(wallet.balance || 0).toFixed(2)}</TableCell>
                        <TableCell>${typeof wallet.credit_limit === 'number' ? wallet.credit_limit.toFixed(2) : parseFloat(wallet.credit_limit || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Button variant="contained" size="small" onClick={() => handleOpenRechargeModal(wallet.user_id)}>
                            Recargar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalWallets}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Billeteras por página:"
              />
            </CardContent>
          </Card>
        )}
        
        {selectedUserId && (
          <AddCreditAdminModal
            open={isAddCreditModalOpen}
            onClose={() => setAddCreditModalOpen(false)}
            userId={selectedUserId}
            onCreditAdded={handleCreditAdded}
          />
        )}
      </Container>
    </Fade>
  );
};

export default AllWalletsPage;